import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Subscription } from '../services/subscriptionService';
import subscriberService, { Subscriber } from '../services/subscriberService';
import SubscriberSelector from './SubscriberSelector';

interface CreateSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (subscription: Partial<Subscription>) => Promise<void>;
}

const CreateSubscriptionModal: React.FC<CreateSubscriptionModalProps> = ({
  isOpen,
  onClose,
  onSubmit
}) => {
  const [formData, setFormData] = useState<Partial<Subscription>>({
    subscriber_id: undefined,
    cost: 0,
    periodicity: 'monthly',
    billing_day: 1,
    duration: 12,
    status: 'active'
  });
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [loadingSubscribers, setLoadingSubscribers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSubscribers = async () => {
    console.log('CreateSubscriptionModal - loadSubscribers called');
    try {
      setLoadingSubscribers(true);
      const data = await subscriberService.getSubscribers();
      debugger;
      console.log('CreateSubscriptionModal - loaded subscribers:', data);
      setSubscribers(data);
    } catch (err) {
      console.error('CreateSubscriptionModal - error loading subscribers:', err);
      setError('Erro ao carregar assinantes');
    } finally {
      setLoadingSubscribers(false);
    }
  };

  useEffect(() => {
    console.log('CreateSubscriptionModal - useEffect triggered, isOpen:', isOpen);
    if (isOpen) {
      loadSubscribers();
    } else {
      setFormData({
        subscriber_id: undefined,
        cost: 0,
        periodicity: 'monthly',
        billing_day: 1,
        duration: 12,
        status: 'active'
      });
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'cost' || name === 'billing_day' || name === 'duration' || name === 'subscriber_id'
        ? Number(value)
        : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Transform cost to total_amount for the API
      const { cost, ...rest } = formData;
      const payload = {
        ...rest,
        total_amount: cost
      };
      await onSubmit(payload);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar assinatura');
    } finally {
      setLoading(false);
    }
  };

  const getBillsPreview = () => {
    const { periodicity, duration, billing_day } = formData;
    if (!periodicity || !duration || !billing_day) return null;

    const numBills = periodicity === 'monthly' ? duration : Math.floor(duration / 12);
    const periodicityText = periodicity === 'monthly' ? 'mensais' : 'anuais';

    return `Serão criadas ${numBills} cobranças ${periodicityText} com vencimento no dia ${billing_day} de cada ${periodicity === 'monthly' ? 'mês' : 'ano'}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-brown-800">Criar Assinatura</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} className="text-gray-600" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto p-6 space-y-6 flex-1">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            {/* Assinante */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Assinante *
              </label>
              <SubscriberSelector
                subscribers={subscribers}
                selectedSubscriberId={formData.subscriber_id}
                onSelect={(subscriberId) => setFormData(prev => ({ ...prev, subscriber_id: subscriberId }))}
                placeholder="Selecione um assinante"
                isLoading={loadingSubscribers}
              />
            </div>

            {/* Valor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Valor (R$) *
              </label>
              <input
                type="number"
                name="cost"
                value={formData.cost}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                placeholder="99.90"
                step="0.01"
                min="0.01"
                required
              />
            </div>

            {/* Periodicidade */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Periodicidade *
              </label>
              <select
                name="periodicity"
                value={formData.periodicity}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                required
              >
                <option value="monthly">Mensal</option>
                <option value="yearly">Anual</option>
              </select>
            </div>

            {/* Dia de Vencimento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dia de Vencimento *
              </label>
              <select
                name="billing_day"
                value={formData.billing_day}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                required
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                  <option key={day} value={day}>
                    Dia {day}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Se o mês não tiver esse dia, será usado o último dia do mês
              </p>
            </div>

            {/* Duração */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duração (meses) *
              </label>
              <input
                type="number"
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-transparent"
                placeholder="12"
                min="1"
                required
              />
              <p className="mt-1 text-xs text-gray-500">
                Ex: 12 meses = 1 ano, 24 meses = 2 anos
              </p>
            </div>

            {/* Preview */}
            {getBillsPreview() && (
              <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded">
                <p className="font-medium">Preview:</p>
                <p className="text-sm">{getBillsPreview()}</p>
              </div>
            )}
          </div>

          {/* Botões */}
          <div className="flex gap-4 justify-end p-6 border-t border-gray-200 bg-white">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-6 py-2 bg-gold-500 text-white rounded-lg hover:bg-gold-600 transition-colors disabled:opacity-50"
              disabled={loading}
            >
              {loading ? 'Criando...' : 'Criar Assinatura'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateSubscriptionModal;
